import moment from 'moment'
import React from 'react'
import styled from 'styled-components'

import { ReactComponent as ClockIcon } from '../../assets/clock.svg'
import { ReactComponent as TempIcon } from '../../assets/temp.svg'
import { ReactComponent as HumidityIcon } from '../../assets/humidity.svg'
import { ReactComponent as RainIcon } from '../../assets/rain.svg'
import { ReactComponent as PrecipitationIcon } from '../../assets/precipitation.svg'
import { ReactComponent as WindIcon } from '../../assets/wind.svg'
import { assessGroupReasons } from '../../utils/assessment'

// Matches the mobile AssessmentGroupModal card: white background, condition
// colour stripe on the left, uppercase "time until" heading, dark time text.
const Root = styled.div({
  display: 'flex',
  flexDirection: 'row',
  minWidth: '120px',
  backgroundColor: '#FFF',
  borderRadius: '6px',
  overflow: 'hidden',
})

const ConditionIndicator = styled.div(({ color }) => ({
  width: '8px',
  flexShrink: 0,
  backgroundColor: color,
}))

const Content = styled.div({
  padding: '8px',
})

const TimeUntil = styled.div({
  marginBottom: '4px',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.35px',
  color: '#C8C8C8',
})

const FlexRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: '4px 0',
})

const TimeText = styled.span({
  marginLeft: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#000',
})

const InfoText = styled.span({
  marginLeft: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#FF7878',
})

const IconWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',

  width: '24px',
  height: '24px',
})

function AssessmentTooltipContents({ assessmentGroup, product }) {
  const {
    startTime: groupStartTime,
    endTime: groupEndTime,
    assessments,
    color,
  } = assessmentGroup
  const { score } = assessments[0]

  const timeUntil = `${moment(groupStartTime).fromNow(true)} from now`
  const startTime = moment(groupStartTime).format('ddd H:mm')
  const endTime = moment(groupEndTime).format('ddd H:mm')

  // A bar spans every consecutive hour with the same condition, so show a
  // reason if it applies to any hour in the group, not just the first. Reasons
  // come from the same terms as the risk score, so they never contradict the colour.
  const {
    temperatureFeelLike: tempScore,
    relativeHumidity: humidityScore,
    quantitativeRainfall: rainfallScore,
    precipChance: precipScore,
    windSpeed: windScore,
  } = assessGroupReasons(product, assessments)

  const assessmentSummaries = [
    {
      Icon: TempIcon,
      score: tempScore,
      label: 'Cold',
    },
    {
      Icon: RainIcon,
      score: rainfallScore,
      label: 'Rain',
    },
    {
      Icon: HumidityIcon,
      score: humidityScore,
      label: 'Humid',
    },
    {
      Icon: PrecipitationIcon,
      score: precipScore,
      label: 'Precip'
    },
    {
      Icon: WindIcon,
      score: windScore,
      label: 'Wind',
    }
  ]

  return (
    <Root>
      <ConditionIndicator color={color} />

      <Content>
        <TimeUntil>{timeUntil}</TimeUntil>

        <FlexRow>
          <IconWrapper>
            <ClockIcon width={16} height={16} fill="#C8C8C8" />
          </IconWrapper>

          <TimeText>
            {startTime} - {endTime}
          </TimeText>
        </FlexRow>

        {score === 0
          ? null
          : assessmentSummaries.map(({ Icon, score: individualScore, label }) => {
              if (!individualScore || individualScore === 0) {
                return null
              }

              return (
                <FlexRow key={label}>
                  <IconWrapper>
                    <Icon width={24} height={24} fill="#FF7878" />
                  </IconWrapper>

                  <InfoText>{label}</InfoText>
                </FlexRow>
              )
            })}
      </Content>
    </Root>
  )
}

export { AssessmentTooltipContents }
